/**
 * Les MODÈLES DE FEUILLE du bon d'intervention (ADR-0014 §14) : Entretien, Dépannage, Installation / mise en service,
 * Livraison, Location. Chaque nature de bon en porte un ; il décide de ce que l'éditeur propose, de ce que la lecture montre
 * et de ce que le papier imprime — dans l'ordre que Benjamin a validé sur les ébauches du 2026-10-16.
 *
 * Pur, sans accès au navigateur ni à l'API. Le serveur ne retient du modèle que ce qui protège les DONNÉES (machine
 * obligatoire ou non, types de ligne permis, champs exigés) ; la mise en page vit ici. Les deux listes de modèles et de types
 * de ligne sont tenues égales à celles du serveur (`modeles-bon.ts` de l'API) par un test de chaque côté.
 *
 * AUCUN prix, AUCUN total : un bon n'en porte pas (ADR-0011).
 */
import type { ChoixDossier, FacturationBon, GenreLigneBon, ModeleBon } from '../api/client';

export const MODELES_BON: readonly ModeleBon[] = ['MAINTENANCE', 'REPAIR', 'INSTALLATION', 'DELIVERY', 'RENTAL'];

/** Le modèle d'une nature dont on ne connaît pas (encore) la liste : l'entretien, comme avant l'arrivée des modèles. */
export const MODELE_PAR_DEFAUT: ModeleBon = 'MAINTENANCE';

export const LIBELLE_MODELE: Readonly<Record<ModeleBon, string>> = {
  MAINTENANCE: 'Entretien / maintenance',
  REPAIR: 'Dépannage',
  INSTALLATION: 'Installation / mise en service',
  DELIVERY: 'Livraison',
  RENTAL: 'Location',
};

/** Ce que chaque modèle dit de lui-même à qui en choisit un (réglages des bons). */
export const DESCRIPTION_MODELE: Readonly<Record<ModeleBon, string>> = {
  MAINTENANCE: 'Contrôles, valeurs relevées, entretien réalisé (palier de la gamme), articles consommés, suites à donner.',
  REPAIR: 'Problème signalé, travaux réalisés, contrôles, valeurs relevées, articles consommés, suites à donner.',
  INSTALLATION: 'Contrôles de mise en service, valeurs (dont tension et intensité par phase), dossier technique remis, matériels et articles.',
  DELIVERY: 'Matériel livré avec ses numéros de série, contrôles à la réception, réserves du client. La machine est facultative.',
  RENTAL: 'Mise à disposition et retour prévu, valeurs au départ et au retour, état de la machine à la remise, accessoires remis.',
};

export const LIBELLE_FACTURATION: Readonly<Record<FacturationBon, string>> = {
  CONTRACT: 'Contrat annuel',
  BILLABLE: 'Facturé à la visite',
};

export const FACTURATIONS: readonly FacturationBon[] = ['CONTRACT', 'BILLABLE'];

/** La classe de pastille de la facturation : verte sous contrat, orange quand elle est facturée. */
export function classeFacturation(facturation: FacturationBon): string {
  return facturation === 'CONTRACT' ? 'facturation-contrat' : 'facturation-facture';
}

export const LIBELLE_DOSSIER: Readonly<Record<ChoixDossier, string>> = {
  HANDED: 'remis en main propre',
  IN_CABINET: 'laissé dans l’armoire électrique',
};

export const CHOIX_DOSSIER: readonly ChoixDossier[] = ['HANDED', 'IN_CABINET'];

/** Les rubriques d'une feuille. L'ordre de `ordre` est celui de l'éditeur, de la lecture et du papier. */
export type Rubrique =
  | 'probleme'
  | 'travaux'
  | 'controles'
  | 'valeurs'
  | 'realise'
  | 'miseEnService'
  | 'dossier'
  | 'periode'
  | 'lignes'
  | 'reserves'
  | 'suites'
  | 'observations';

/** Où va le n° de série d'un matériel : dans une colonne à lui (livraison) ou à la suite de la désignation (installation). */
export type PlaceSerie = 'COLONNE' | 'DANS_DESIGNATION' | 'AUCUNE';

export interface ConfigModele {
  /** La machine est-elle obligatoire ? Seule la livraison peut se faire sur un site seul. */
  readonly machineFacultative: boolean;
  /** « Technicien » ou « Techniciens » (selon leur nombre), sauf la livraison : « Livré par ». */
  readonly libelleTechnicien: { readonly singulier: string; readonly pluriel: string };
  readonly titreControles: string;
  readonly titreValeurs: string;
  readonly titreLignes: string;
  /** Les types de ligne permis, dans l'ordre des boutons d'ajout. */
  readonly genres: readonly GenreLigneBon[];
  /** Le libellé des boutons d'ajout de ligne : « + Article ». */
  readonly boutonsLignes: readonly { readonly kind: GenreLigneBon; readonly libelle: string }[];
  /** La colonne « Type » du tableau des lignes (absente de la livraison et de la location). */
  readonly colonneType: boolean;
  /** La colonne « Référence » du tableau des lignes (absente de la location : un accessoire n'a pas de référence). */
  readonly colonneReference: boolean;
  readonly serie: PlaceSerie;
  /** Les relevés de compteur et le palier réalisé : la livraison n'en porte aucun. */
  readonly releves: boolean;
  readonly palier: boolean;
  readonly suites: boolean;
  /** Au départ / au retour : la location relève les heures deux fois. */
  readonly departRetour: boolean;
  readonly signatureClient: string;
  readonly signatureTechnicien: string;
  readonly ordre: readonly Rubrique[];
}

const LIGNES_COURANTES = [
  { kind: 'PART', libelle: '+ Pièce en plus' },
  { kind: 'LABOR', libelle: '+ Main d’œuvre en plus' },
  { kind: 'TRAVEL', libelle: '+ Déplacement en plus' },
  { kind: 'FLAT_FEE', libelle: '+ Forfait' },
] as const satisfies ConfigModele['boutonsLignes'];

const TECHNICIENS = { singulier: 'Technicien', pluriel: 'Techniciens' } as const;

export const CONFIG_MODELES: Readonly<Record<ModeleBon, ConfigModele>> = {
  MAINTENANCE: {
    machineFacultative: false,
    libelleTechnicien: TECHNICIENS,
    titreControles: 'Contrôles et inspections',
    titreValeurs: 'Valeurs relevées',
    titreLignes: 'Articles consommés',
    genres: ['PART', 'LABOR', 'TRAVEL', 'FLAT_FEE'],
    boutonsLignes: LIGNES_COURANTES,
    colonneType: true,
    colonneReference: true,
    serie: 'AUCUNE',
    releves: true,
    palier: true,
    suites: true,
    departRetour: false,
    signatureClient: 'Signature client',
    signatureTechnicien: 'Signature technicien',
    ordre: ['controles', 'valeurs', 'realise', 'lignes', 'suites', 'observations'],
  },
  REPAIR: {
    machineFacultative: false,
    libelleTechnicien: TECHNICIENS,
    titreControles: 'Contrôles et inspections',
    titreValeurs: 'Valeurs relevées',
    titreLignes: 'Articles consommés',
    genres: ['PART', 'LABOR', 'TRAVEL', 'FLAT_FEE'],
    boutonsLignes: LIGNES_COURANTES,
    colonneType: true,
    colonneReference: true,
    serie: 'AUCUNE',
    releves: true,
    palier: false,
    suites: true,
    departRetour: false,
    signatureClient: 'Signature client',
    signatureTechnicien: 'Signature technicien',
    ordre: ['probleme', 'travaux', 'controles', 'valeurs', 'lignes', 'suites', 'observations'],
  },
  INSTALLATION: {
    machineFacultative: false,
    libelleTechnicien: TECHNICIENS,
    titreControles: 'Contrôles de mise en service',
    titreValeurs: 'Valeurs à la mise en service',
    titreLignes: 'Matériels / articles',
    genres: ['EQUIPMENT', 'PART', 'LABOR', 'TRAVEL', 'FLAT_FEE'],
    boutonsLignes: [{ kind: 'EQUIPMENT', libelle: '+ Matériel' }, ...LIGNES_COURANTES],
    colonneType: true,
    colonneReference: true,
    serie: 'DANS_DESIGNATION',
    releves: true,
    palier: false,
    suites: true,
    departRetour: false,
    signatureClient: 'Signature client',
    signatureTechnicien: 'Signature technicien',
    ordre: ['controles', 'valeurs', 'miseEnService', 'dossier', 'lignes', 'suites', 'observations'],
  },
  DELIVERY: {
    machineFacultative: true,
    libelleTechnicien: { singulier: 'Livré par', pluriel: 'Livré par' },
    titreControles: 'À la réception',
    titreValeurs: 'Valeurs relevées',
    titreLignes: 'Matériel livré',
    genres: ['EQUIPMENT', 'PART'],
    boutonsLignes: [
      { kind: 'EQUIPMENT', libelle: '+ Matériel' },
      { kind: 'PART', libelle: '+ Article' },
    ],
    colonneType: false,
    colonneReference: true,
    serie: 'COLONNE',
    releves: false,
    palier: false,
    suites: false,
    departRetour: false,
    signatureClient: 'Signature client (réception)',
    signatureTechnicien: 'Signature livreur',
    ordre: ['lignes', 'controles', 'reserves', 'observations'],
  },
  RENTAL: {
    machineFacultative: false,
    libelleTechnicien: TECHNICIENS,
    titreControles: 'État de la machine à la remise',
    titreValeurs: 'Valeurs relevées',
    titreLignes: 'Accessoires remis',
    genres: ['ACCESSORY'],
    boutonsLignes: [{ kind: 'ACCESSORY', libelle: '+ Accessoire' }],
    colonneType: false,
    colonneReference: false,
    serie: 'AUCUNE',
    releves: true,
    palier: false,
    suites: false,
    departRetour: true,
    signatureClient: 'Signature client',
    signatureTechnicien: 'Signature technicien',
    ordre: ['periode', 'valeurs', 'controles', 'lignes', 'observations'],
  },
};

/** Le modèle d'une nature (ou d'un bon) ; l'entretien quand on ne sait pas. */
export function modeleDe(source: { readonly layout?: ModeleBon | undefined } | null | undefined): ModeleBon {
  return source?.layout ?? MODELE_PAR_DEFAUT;
}

export function configDe(modele: ModeleBon): ConfigModele {
  return CONFIG_MODELES[modele];
}

/** « Technicien » ou « Techniciens », ou « Livré par » : selon le modèle et le nombre de noms. */
export function libelleTechniciens(modele: ModeleBon, nombre: number): string {
  const l = CONFIG_MODELES[modele].libelleTechnicien;
  return nombre > 1 ? l.pluriel : l.singulier;
}

/** Un type de ligne est-il permis par le modèle ? (Miroir de `GENRES_PERMIS` du serveur.) */
export function genrePermis(modele: ModeleBon, kind: GenreLigneBon): boolean {
  return CONFIG_MODELES[modele].genres.includes(kind);
}

/** Les types de ligne d'un bon que son modèle ne permet pas : à retirer avant de soumettre. */
export function genresInterdits(modele: ModeleBon, genres: readonly GenreLigneBon[]): GenreLigneBon[] {
  return genres.filter((g) => !genrePermis(modele, g));
}
